import { captureMessage } from '@sentry/browser';
import cookie from 'react-cookies';
import { CSRF_TOKEN_NAME, IS_ADMIN_HOST } from 'utils/envVars';

/** Reads a header from axios response headers or a jQuery jqXHR. */
const headerValue = (headers: any, name: string): string | undefined =>
  typeof headers?.getResponseHeader === 'function'
    ? headers.getResponseHeader(name) ?? undefined
    : headers?.[name.toLowerCase()];

// The admin-host edge Snippet stamps x-ps-admin-edge on every response it
// proxies. Cloudflare Access answers an expired session before the Snippet
// runs, so an unmarked 401 means re-authenticate with SSO, not refresh the JWT.
export const isAccessExpired = (status: number | undefined, headers: any) =>
  IS_ADMIN_HOST && status === 401 && !headerValue(headers, 'x-ps-admin-edge');

// Admin hosts get the __Host-crew_ cookie; the base name covers sessions
// minted before the crew cookie existed.
export const readRefreshCsrfToken = () =>
  (IS_ADMIN_HOST && cookie.load(`__Host-crew_${CSRF_TOKEN_NAME}`)) ||
  cookie.load(CSRF_TOKEN_NAME);

const RELOAD_GUARD_KEY = 'ps-access-expiry-reload-at';
const RELOAD_GUARD_MS = 10000;
let reloadPending = false;

// The reload's top-level navigation gets Access's SSO redirect. If /api keeps
// answering unmarked 401s anyway (Snippet down, Cloudflare error page), the
// guard suppresses the reload and callers fall back to the normal 401 path.
export const reloadForAccessExpiry = (): 'reloading' | 'suppressed' => {
  if (reloadPending) return 'reloading';
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY));
    if (Date.now() - last < RELOAD_GUARD_MS) {
      captureMessage('admin access-expiry reload suppressed', 'warning');
      return 'suppressed';
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // Storage blocked: reload unguarded.
  }
  reloadPending = true;
  window.location.reload();
  return 'reloading';
};

/**
 * Parses a JWT token and returns the decoded payload.
 *
 * @param token - The JWT token string to parse
 * @returns The decoded payload as a JSON object
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseJwt = (token: string): Record<string, any> => {
  if (!token || typeof token !== 'string') {
    throw new Error('Invalid token: token must be a non-empty string');
  }

  const parts = token.split('.');
  if (parts.length !== 3) {
    throw new Error(
      'Invalid token: JWT must have 3 parts (header.payload.signature)',
    );
  }

  const base64Url = parts[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

  try {
    const payload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(
          (character) =>
            `%${`00${character.charCodeAt(0).toString(16)}`.slice(-2)}`,
        )
        .join(''),
    );
    return JSON.parse(payload);
  } catch (error) {
    throw new Error(
      `Failed to parse JWT: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`,
    );
  }
};
